import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Chip, Flex, Heading, Pagination, Spinner, Text, TextField } from "@telia/teddy";
import { API_URL_SUPERADMIN_FEEDBACK } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { type FeedbackEntry } from "./feedbackTypes";
import ReviewCard from "./ReviewCard";

const PAGE_SIZE = 20;
const STAR_VALUES = ["1", "2", "3", "4", "5"];
const WITH_DESCRIPTION = "with_description";
const SHOW_ALL = "all";

const AllReviews: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [reviews, setReviews] = useState<FeedbackEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const descriptionFilter = searchParams.get("desc") === SHOW_ALL ? SHOW_ALL : WITH_DESCRIPTION;
    const selectedStars = useMemo(
        () => (searchParams.get("stars") ?? "").split(",").filter((s) => STAR_VALUES.includes(s)),
        [searchParams],
    );
    const search = searchParams.get("search") ?? "";
    const pageParam = Number(searchParams.get("page"));
    const page = pageParam >= 1 ? pageParam : 1;

    const updateParams = useCallback(
        (updates: Record<string, string | null>) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    for (const [key, value] of Object.entries(updates)) {
                        if (value) {
                            next.set(key, value);
                        } else {
                            next.delete(key);
                        }
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    useEffect(() => {
        const fetchReviews = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await retailerFetch(`${API_URL_SUPERADMIN_FEEDBACK}?feedbackType=review`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: FeedbackEntry[] = await res.json();
                setReviews(data.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
            } catch (err) {
                setError(err instanceof Error ? err.message : "Kunne ikke hente vurderinger");
            } finally {
                setLoading(false);
            }
        };
        fetchReviews();
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return reviews.filter((e) => {
            if (descriptionFilter === WITH_DESCRIPTION && !e.description) return false;
            if (selectedStars.length > 0 && !selectedStars.includes(String(e.score))) return false;
            if (!q) return true;
            return (
                e.description?.toLowerCase().includes(q) ||
                e.agentName?.toLowerCase().includes(q) ||
                e.dealerName?.toLowerCase().includes(q)
            );
        });
    }, [reviews, descriptionFilter, selectedStars, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handlePageChange = (newPage: number) => {
        updateParams({ page: newPage > 1 ? String(newPage) : null });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (loading) {
        return (
            <Box className="all-reviews__loading">
                <Spinner size="md" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="all-reviews__loading">
                <Text>{error}</Text>
            </Box>
        );
    }

    return (
        <>
            <Heading mb="100" as="h3" variant="title-200">
                Vurderinger ({filtered.length})
            </Heading>
            <Flex direction="column" gap="200" mb="300">
                <Chip
                    type="single"
                    value={descriptionFilter}
                    onValueChange={(val) => {
                        if (val) updateParams({ desc: val === SHOW_ALL ? val : null, page: null });
                    }}
                    variant="filter"
                >
                    <Chip.Item value={SHOW_ALL}>Vis alle</Chip.Item>
                    <Chip.Item value={WITH_DESCRIPTION}>Kun de med beskrivelse</Chip.Item>
                </Chip>
                <Chip
                    type="multiple"
                    value={selectedStars}
                    onValueChange={(vals) =>
                        updateParams({ stars: vals.length > 0 ? [...vals].sort().join(",") : null, page: null })
                    }
                    variant="filter"
                    aria-label="Filtrer på antall stjerner"
                >
                    {STAR_VALUES.map((stars) => (
                        <Chip.Item key={stars} value={stars}>
                            {stars} {stars === "1" ? "stjerne" : "stjerner"}
                        </Chip.Item>
                    ))}
                </Chip>
                <TextField>
                    <TextField.Input
                        placeholder="Søk i beskrivelse, agent eller forhandler..."
                        aria-label="Søk i vurderinger"
                        value={search}
                        onChange={(e) => updateParams({ search: e.target.value || null, page: null })}
                    />
                </TextField>
            </Flex>

            {filtered.length === 0 ? (
                <Text className="all-reviews__empty">Ingen vurderinger funnet.</Text>
            ) : (
                <>
                    {pageItems.map((entry) => (
                        <ReviewCard key={entry.id} entry={entry} searchQuery={search.trim()} />
                    ))}

                    {totalPages > 1 && (
                        <Pagination aria-label="Sidenavigasjon" className="all-reviews__pagination">
                            <Pagination.Navigation
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                side="prev"
                                showLabels={false}
                            />
                            <Pagination.PageNumbers
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                maxVisiblePages={5}
                            />
                            <Pagination.Navigation
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                side="next"
                                showLabels={false}
                            />
                        </Pagination>
                    )}
                </>
            )}
        </>
    );
};

export default AllReviews;
